import Vue from "vue";
import { postApi } from "@/utils/http";
import {
  getChocoPackagesFromFilter,
  mergePackages,
  getChocoPackageFilter,
  isSamePackage,
  correctMissingChocoPackages,
  stripPackages,
} from "@/utils/mdc";

import { fetchPackagesStats } from "@/utils/groupPackage";

import { transformPackages } from "@/utils/packages";
import { getPackageNameVersionAndHash } from "../utils/mdc";
import { orderPackages } from "@/utils/packageOrder";
import { getReversed } from "@/utils/order";

export default {
  namespaced: true,
  state: {
    computerPackages: {},
    fullyLoadedPackages: {},
    mergedPackages: {},
    mdcPackages: [],
    chocoPackages: [],
    computerUID: null,
  },
  mutations: {
    selectItem(state, p) {
      for (const key of Object.keys(state.mergedPackages)) {
        for (const x of state.mergedPackages[key]) {
          if (x.selectedForAction) x.selectedForAction = false;
        }
      }

      if (p) Vue.set(p, "selectedForAction", true);
    },
    removeMergedPackage(state, p) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const index = state.mergedPackages[name + uid].findIndex((x) =>
        isSamePackage(x, p)
      );

      Vue.delete(state.mergedPackages[name + uid], index);
    },
    removeMdcPackage(state, p) {
      const index = state.mdcPackages.findIndex((x) => isSamePackage(x, p));
      Vue.delete(state.mdcPackages, index);
    },
    mergeMergedPackage(state, p) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const packages = state.mergedPackages[name + uid];
      if (packages) {
        const index = packages.findIndex((x) => isSamePackage(x, p));
        if (index !== -1) {
          const item = state.mergedPackages[name + uid][index];
          Vue.set(state.mergedPackages[name + uid], index, { ...item, ...p });
        } else state.mergedPackages[name + uid].unshift(p);
      } else Vue.set(state.mergedPackages, [name + uid], [p]);
    },
    mergeMdcPackage(state, p) {
      const index = state.mdcPackages.findIndex((x) => isSamePackage(x, p));
      if (index !== -1) {
        const item = state.mdcPackages[index];
        Vue.set(state.mdcPackages, index, { ...item, ...p });
      } else state.mdcPackages.unshift(p);
    },
    editMergedPackage(state, { p, obj }) {
      const { name, uid } = getPackageNameVersionAndHash(p);

      if (state.mergedPackages[name + uid]) {
        const index = state.mergedPackages[name + uid].findIndex((x) =>
          isSamePackage(x, p)
        );

        const item = state.mergedPackages[name + uid][index];

        if (item) {
          Vue.set(state.mergedPackages[name + uid], index, { ...item, ...obj });
        }
      }
    },
    replaceMergedPackage(state, { p, obj }) {
      const { name, uid } = getPackageNameVersionAndHash(p);

      const index = state.mergedPackages[name + uid].findIndex((x) =>
        isSamePackage(x, p)
      );

      Vue.set(state.mergedPackages[name + uid], index, obj);
    },
    addMergedPackage(state, p) {
      const { name, uid } = getPackageNameVersionAndHash(p);

      if (!state.mergedPackages[name + uid]) {
        state.mergedPackages[name + uid] = [];
      }

      state.mergedPackages[name + uid].unshift(p);
    },
    setMergedPackageVersions(state, ps) {
      if (ps.length > 0) {
        const { name, uid } = getPackageNameVersionAndHash(ps[0]);
        Vue.set(state.mergedPackages, [name + uid], ps);
        Vue.set(state.fullyLoadedPackages, [name + uid], true);
      }
    },
    setMergedPackages(state, packages) {
      state.mergedPackages = packages;
    },

    setChocoPackages(state, chocoPackages) {
      state.chocoPackages = chocoPackages;
    },
    setMdcPackages(state, mdcPackages) {
      state.mdcPackages = mdcPackages;
    },
    setComputerPackages(state, cps) {
      if (cps.packages.length !== 0) {
        const { hash } = getPackageNameVersionAndHash(cps.packages[0]);
        state.computerPackages[hash] = cps.packages;
      }
    },
    mergeComputerPackage(state, p) {
      const { name, version, uid } = getPackageNameVersionAndHash(p);
      const location = name + version + uid;

      const packages = state.computerPackages[location];
      if (packages) {
        const index = packages.findIndex(
          (x) => isSamePackage(x, p) && x.computerUID === p.computerUID
        );

        if (index !== -1) {
          const item = state.computerPackages[location][index];
          Vue.set(state.computerPackages[location], index, {
            ...item,
            ...p,
          });
        } else state.computerPackages[location].unshift(p);
      } else Vue.set(state.computerPackages, [location], [p]);
    },
    clear(state) {
      state.computerPackages = {};
      state.mergedPackages = {};
      state.fullyLoadedPackages = {};
      state.mdcPackages = [];
      state.chocoPackages = [];
      state.computerUID = null;
    },
  },
  actions: {
    async orderMergedPackages({ commit, state, dispatch, rootState }, order) {
      const stateOrder = rootState.settings.user.groupPackages?.order;
      order = getReversed(stateOrder, order);
      dispatch(
        "settings/setUser",
        { path: "groupPackages", key: "order", value: order },
        { root: true }
      );

      return orderPackages(
        state.mergedPackages,
        order
      ).then((transformedPackages) =>
        commit("setMergedPackages", transformedPackages)
      );
    },

    async getDuplicateDomainPackages({ commit, state }, { name, domainUID }) {
      return await postApi("get/domainGroupPackages", {
        domainUID,
        name,
      });
    },

    async fetchMdcPackages({ commit, state }, data) {
      if (state.mdcPackages.length === 0 || data.force) {
        const mdcRes = await postApi("get/latestGroupPackages", {
          domainUID: data.domainUID,
          groupUID: data.groupUID,
        });

        commit("setMdcPackages", mdcRes[0]);
      }
    },
    async getAccountGroupPackages({ state }, { domainUID }) {
      return await postApi("get/accountGroupPackages", { domainUID });
    },
    async fetchAndMerge(
      { dispatch, commit, state, rootState, rootGetters },
      data
    ) {
      await dispatch("fetchMdcPackages", data);
      await dispatch(
        "source/fetch",
        { domainUID: data.domainUID },
        { root: true }
      );

      const filters = await getChocoPackageFilter(
        rootState.source.sources,
        state.mdcPackages
      );

      const chocoRes = await getChocoPackagesFromFilter(filters);
      const chocoPackages = stripPackages(chocoRes);

      const packages = await mergePackages(
        state.mdcPackages,
        chocoPackages,
        true
      );

      await dispatch(
        "group/fetch",
        { domainUID: data.domainUID },
        { root: true }
      );

      const group = rootGetters["group/single"](data.groupUID);

      return orderPackages(
        packages,
        rootState.settings.user.groupPackages.order
      )
        .then(transformPackages)
        .then((transformedPackages) => {
          commit("setMergedPackages", transformedPackages);
          fetchPackagesStats(packages, group, data.domainUID, true, true);
        });
    },

    async searchFetchAndMerge(
      { dispatch, commit, state, rootGetters, rootState },
      data
    ) {
      await dispatch("fetchMdcPackages", data);

      const chocoRes = await getChocoPackagesFromFilter(data.filters, false);

      const chocoPackages = await correctMissingChocoPackages(
        state.mdcPackages,
        chocoRes
      );

      const packages = await mergePackages(
        chocoPackages,
        state.mdcPackages,
        true,
        true
      );

      commit(
        "setMergedPackages",
        await transformPackages(
          await orderPackages(
            packages,
            rootState.settings.user.groupPackages.order
          )
        )
      );

      await dispatch(
        "group/fetch",
        { domainUID: data.domainUID },
        { root: true }
      );

      const group = rootGetters["group/single"](data.groupUID);

      fetchPackagesStats(packages, group, data.domainUID, true);
    },
  },

  getters: {
    selectedItem: (state) => {
      for (const key of Object.keys(state.mergedPackages)) {
        const item = state.mergedPackages[key].find((x) => x.selectedForAction);
        if (item) return item;
      }
    },
    packageGroup: (state) => ({ p, filter }) => {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const packages = state.mergedPackages[name + uid];

      if (packages) {
        if (filter) return packages.filter(filter);
        else packages;
      }

      return [];
    },

    mergedPackage: (state) => (p) => {
      try {
        const { name, uid } = getPackageNameVersionAndHash(p);
        const packages = state.mergedPackages[name + uid];
        if (packages) {
          return packages.find((x) => isSamePackage(x, p));
        }

        return null;
      } catch (err) {
        console.log("mergedPackage", err);
      }
    },
  },
  modules: {},
};
